/**
 * Public API landing + reference (PLAN_API.md §12.1).
 *
 * Fully translated into the eight public locales. The first version kept the reference
 * in English on the theory that a contract should exist in exactly one language; the
 * owner overruled it, and they are right about the thing that matters — half a page in
 * a language you do not read is a page that says the product is not for you.
 *
 * Prose lives in `src/lib/api-docs-content.ts`. Code samples, header names, field names
 * and error `code` values stay untranslated on purpose: they are identifiers a client
 * compares against, and a translated identifier is a bug.
 */
import { createFileRoute, notFound, Link } from "@tanstack/react-router";

import { isPublicLocale, PUBLIC_LOCALES } from "@/components/legal-page";
import { SiteFooter } from "@/components/site-footer";
import { TopBar } from "@/components/top-bar";
import { apiDocsContent, ERROR_CODES } from "@/lib/api-docs-content";
import { useAuth } from "@/lib/auth";
import { SITE_URL } from "@/lib/site-url";

export const Route = createFileRoute("/$lang/api-docs")({
  beforeLoad: ({ params }) => {
    if (!isPublicLocale(params.lang)) throw notFound();
  },
  head: ({ params }) => {
    const lang = params.lang;
    const t = apiDocsContent(lang);
    const canonical = `${SITE_URL}/${lang}/api-docs`;
    return {
      meta: [
        { title: t.metaTitle },
        { name: "description", content: t.metaDescription },
        { name: "robots", content: "index, follow" },
        { property: "og:title", content: t.metaTitle },
        { property: "og:description", content: t.metaDescription },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonical },
      ],
      links: [
        { rel: "canonical", href: canonical },
        { rel: "alternate", hrefLang: "x-default", href: `${SITE_URL}/en/api-docs` },
        ...PUBLIC_LOCALES.map((l) => ({
          rel: "alternate",
          hrefLang: l,
          href: `${SITE_URL}/${l}/api-docs`,
        })),
      ],
    };
  },
  component: ApiDocsPage,
});

function Code({ children }: { children: string }) {
  return (
    <pre className="overflow-x-auto rounded-lg border border-border bg-card/60 p-4 text-xs leading-relaxed">
      <code>{children}</code>
    </pre>
  );
}

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-20">
      <h2 className="text-xl font-semibold text-foreground">{title}</h2>
      <div className="mt-3 space-y-3 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}

const QUICKSTART = `# 1. Create a key in your dashboard, then:
export SE_KEY="se_live_XXXXXXXXXXXX_..."

# 2. What will this cost?
curl -s https://smsempire.com/api/v1/prices?service=tg \\
  -H "Authorization: Bearer $SE_KEY"

# 3. Buy a number. The Idempotency-Key is required.
curl -s https://smsempire.com/api/v1/activations \\
  -H "Authorization: Bearer $SE_KEY" \\
  -H "Idempotency-Key: $(uuidgen)" \\
  -H "Content-Type: application/json" \\
  -d '{"service":"tg","max_price":"0.35"}'

# 4. Collect the code (or let the webhook push it to you).
curl -s https://smsempire.com/api/v1/activations/90210 \\
  -H "Authorization: Bearer $SE_KEY"`;

const VERIFY_PY = `import hmac, hashlib, time
from flask import request, abort

SECRET = "whsec_..."          # from your dashboard
TOLERANCE = 300               # seconds

def verify(request):
    raw = request.get_data()                       # RAW bytes, before json parsing
    ts = request.headers.get("X-SmsEmpire-Timestamp", "")
    sig_header = request.headers.get("X-SmsEmpire-Signature", "")

    if abs(time.time() - int(ts)) > TOLERANCE:     # blocks replays
        abort(400)

    expected = hmac.new(SECRET.encode(),
                        f"{ts}.".encode() + raw,
                        hashlib.sha256).hexdigest()

    # More than one v1= during a secret rotation; any match is valid.
    sigs = [p.split("=", 1)[1] for p in sig_header.split(",") if p.startswith("v1=")]
    if not any(hmac.compare_digest(expected, s) for s in sigs):
        abort(400)`;

const VERIFY_JS = `const crypto = require("crypto");

// express.raw({type: "application/json"}) — you need the RAW body, not the parsed one.
function verify(req, secret) {
  const ts = req.get("X-SmsEmpire-Timestamp") || "";
  const header = req.get("X-SmsEmpire-Signature") || "";

  if (Math.abs(Date.now() / 1000 - Number(ts)) > 300) return false;

  const expected = crypto
    .createHmac("sha256", secret)
    .update(Buffer.concat([Buffer.from(ts + "."), req.body]))
    .digest("hex");

  return header
    .split(",")
    .filter((p) => p.startsWith("v1="))
    .some((p) =>
      crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(p.slice(3))),
    );
}`;

const EVENT = `POST /your/webhook
X-SmsEmpire-Event: activation.code_received
X-SmsEmpire-Delivery: evt_01j8z...        <- deduplicate on this
X-SmsEmpire-Timestamp: 1765432991
X-SmsEmpire-Signature: v1=3a7f...

{
  "id": "evt_01j8z...",
  "type": "activation.code_received",
  "created_at": "2026-08-14T12:03:11Z",
  "data": {
    "activation_id": 90210,
    "service": "tg",
    "country": "2",
    "number": "+79991234567",
    "code": "483920",
    "status": "received",
    "price": "0.28000"
  }
}`;

function ApiDocsPage() {
  const { lang } = Route.useParams();
  const t = apiDocsContent(lang);
  const { user } = useAuth();

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <TopBar />

      <main className="mx-auto w-full max-w-3xl flex-1 px-6 py-12">
        <header>
          <h1 className="text-4xl font-bold tracking-tight text-foreground">{t.heading}</h1>
          <p className="mt-3 text-muted-foreground">{t.subheading}</p>
          <ul className="mt-5 space-y-2 text-sm text-muted-foreground">
            {t.bullets.map((b) => (
              <li key={b} className="flex gap-2">
                <span className="text-primary">→</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <div className="mt-6 flex flex-wrap gap-3">
            {/* Signed-in visitors go straight to their keys. Sending them to the
                register screen bounced them to /dashboard, which reads as the button
                being broken. */}
            <Link
              to={user ? "/api-keys" : "/auth"}
              search={user ? undefined : { tab: "register" }}
              className="inline-flex items-center justify-center rounded-md bg-gradient-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-glow transition-opacity hover:opacity-90"
            >
              {t.ctaKey}
            </Link>
            <a
              href="/api/v1/docs"
              className="inline-flex items-center justify-center rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-card"
            >
              {t.ctaReference}
            </a>
          </div>
        </header>

        <div className="mt-12 space-y-12">
          <Section id="quickstart" title={t.quickstartTitle}>
            <p>{t.quickstartBody}</p>
            <Code>{QUICKSTART}</Code>
          </Section>

          <Section id="conventions" title={t.conventionsTitle}>
            <ul className="list-disc space-y-1.5 pl-5">
              {t.conventions.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </Section>

          <Section id="idempotency" title={t.idempotencyTitle}>
            {t.idempotencyBody.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </Section>

          <Section id="max-price" title={t.maxPriceTitle}>
            <p>{t.maxPriceBody}</p>
          </Section>

          <Section id="webhooks" title={t.webhooksTitle}>
            <p>{t.webhooksBody[0]}</p>
            <Code>{EVENT}</Code>
            <p className="pt-2">{t.webhooksBody[1]}</p>
          </Section>

          <Section id="verify" title={t.verifyTitle}>
            <p>{t.verifyIntro}</p>
            <ol className="list-decimal space-y-1.5 pl-5">
              {t.verifyMistakes.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ol>
            <Code>{VERIFY_PY}</Code>
            <Code>{VERIFY_JS}</Code>
            <p>{t.verifyRotation}</p>
          </Section>

          <Section id="errors" title={t.errorsTitle}>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="text-foreground">
                  <tr>
                    <th className="py-2 pr-4 font-medium">{t.errorsColCode}</th>
                    <th className="py-2 font-medium">{t.errorsColWhat}</th>
                  </tr>
                </thead>
                <tbody>
                  {ERROR_CODES.map((code) => (
                    <tr key={code} className="border-t border-border">
                      {/* The code itself is an identifier, never translated. */}
                      <td className="py-2 pr-4 font-mono">{code}</td>
                      <td className="py-2">{t.errors[code]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section id="lifecycle" title={t.lifecycleTitle}>
            {t.lifecycleBody.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </Section>

          <Section id="limits" title={t.limitsTitle}>
            <p>{t.limitsBody}</p>
          </Section>
        </div>

        <section className="mt-14 rounded-2xl border border-border bg-card p-6 text-center shadow-elegant">
          <h2 className="text-xl font-semibold text-foreground">{t.referenceTitle}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{t.referenceBody}</p>
          <a
            href="/api/v1/docs"
            className="mt-5 inline-flex items-center justify-center rounded-md bg-gradient-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-glow transition-opacity hover:opacity-90"
          >
            {t.referenceCta}
          </a>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
